import React, { useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, FieldArray } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import TextField from '@/components/formik/fields/TextField'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import Dialog from "@mui/material/Dialog";

export type EmailsFormValues = {
    emails: string[]
}

export type FormEmailsProps = {
    initialValues: EmailsFormValues
    isSubmitting?: boolean
    onSubmit: (values: EmailsFormValues) => void
    open: boolean
    onClose: () => void
}

export type conclusionSentencesFormHandle = {
    toggleEditable: () => void
}

const conclusionSentencesValidationSchema = yup.object().shape({
    sentences: yup.array().of(yup.string().email().required()),
})

function FormEmails(props: FormEmailsProps, ref: React.Ref<conclusionSentencesFormHandle>) {
    const { initialValues, isSubmitting, onSubmit, open, onClose } = props
    const { t } = useTranslation()
    const [editable, setEditable] = useState<boolean>(false)

    useImperativeHandle(ref, () => ({
        toggleEditable: () => {
            setEditable(!editable)
        },
    }))

    const handleToggleEditable = () => {
        setEditable(!editable)
    }

    return (
        <Dialog onClose={onClose} open={open} PaperProps={{ sx: { p: '50px', maxWidth: (theme) => theme.breakpoints.values.sm, width: '100%' } }}>
            <Formik initialValues={initialValues} validationSchema={conclusionSentencesValidationSchema} onSubmit={onSubmit}>
                {({ handleSubmit, values, isValid }) => (
                    <Form onSubmit={handleSubmit}>
                        <FieldArray
                            name="emails"
                            render={(arrayHelpers) => (
                                <Stack direction="column" spacing="30px">
                                    {values.emails.map((email, index) => (
                                        <Stack direction="row" spacing="10" alignContent="left" key={index}>
                                            <TextField
                                                readOnly={!editable}
                                                fullWidth
                                                label={`${t('field.email.label')} ${index + 1}`}
                                                name={`emails.${index}`}
                                            />
                                            <IconButton disabled={!editable} onClick={() => arrayHelpers.remove(index)}>
                                                <DeleteIcon htmlColor={!editable ? 'grey' : 'red'} />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                    <Button disabled={!editable} variant="outlined" type="button" onClick={() => arrayHelpers.push('')}>
                                        {t('common:button.add.label')}
                                    </Button>
                                    <Box sx={{ display: 'flex', justifyContent: 'end', mt: '36px' }}>
                                        {editable ? (
                                            <Stack direction="row" spacing={2}>
                                                <Button
                                                    variant="neutral"
                                                    onClick={() => {
                                                        handleToggleEditable()
                                                    }}
                                                >
                                                    {t('common:button.cancel.label')}
                                                </Button>
                                                <LoadingButton disabled={!isValid} color="primary" disableElevation loading={isSubmitting} type="submit" variant="contained">
                                                    {t('common:button.submit.label')}
                                                </LoadingButton>
                                            </Stack>
                                        ) : (
                                            <Button color="primary" disableElevation variant="contained" onClick={handleToggleEditable}>
                                                {t('common:button.modify.label')}
                                            </Button>
                                        )}
                                    </Box>
                                </Stack>
                            )}
                        />
                    </Form>
                )}
            </Formik>
        </Dialog>
    )
}

export default React.forwardRef(FormEmails)
